import React, { useContext, useEffect, useMemo, useState } from 'react';

// helpers
import useFetch from 'hooks/useFetch';
import useTranslation from 'hooks/useTranslation';
import { ActionKeys } from 'components/Forms/TemplateForms/Onboarding/Components/LastStepSubmitButtons';
import { useDispatch } from 'react-redux';
import { onboardingAPI } from 'api/onboarding/onboardingAPI';
import { VerificationContext } from 'modules/Onboarding/Contexts/VerificationContext';
import { OnboardingEntryTypes } from 'enums/onboarding/crm';
import { onboardingAPIAdapter } from 'apiAdapters/onboarding/onboardingAPIAdapter';
import { updateActiveApplication } from 'redux/actions/applications';

// components
import LoadingWrapper from 'components/WrapperComponents/LoadingWrapper';
import ApplicationDocumentationForm, {
  FormValuesModel,
} from 'components/Forms/TemplateForms/Onboarding/ApplicationDocumentationForm';
import { Message } from '@ui';

interface IProps {
  isViewOnly: boolean;
  onAction: (key: ActionKeys | null) => void;
  onboardingData: {
    applicationId: string;
    contactId: string;
    clientGroupId: string;
  };
}

const ApplicationDocumentation = ({
  onAction,
  onboardingData,
  isViewOnly,
}: IProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(['common', 'onboarding']);
  const [isSubmittedForReview, setIsSubmittedForReview] = useState<
    boolean | null
  >(null);
  const verificationContext = useContext(VerificationContext);
  const [updateAnswersTrigger, updateAnswers] = useState<any>();

  const {
    response: onboardingStatusResponse,
    loading: onboardingStatusLoading,
  } = useFetch(
    () =>
      onboardingAPI.fetchStatusForOnboardingItem(
        onboardingData.contactId,
        onboardingData.applicationId,
      ),
    [onboardingData, updateAnswersTrigger],
  );

  const { response: answersResponse, loading: answersLoader } = useFetch(
    () =>
      onboardingAPI.fetchApplicationDocumentationStepAnswers(
        onboardingData.clientGroupId as string,
        onboardingData.contactId,
      ),
    [onboardingData, updateAnswersTrigger],
  );

  useEffect(() => {
    if (onboardingStatusResponse) {
      setIsSubmittedForReview(
        onboardingStatusResponse.informationFilled.applicationDocumentation,
      );

      verificationContext?.setCanSubmitForReview(
        onboardingStatusResponse.informationFilled.applicationDocumentation,
      );
    }
  }, [onboardingStatusResponse, verificationContext]);

  const initialFormValues = useMemo<FormValuesModel | null>(() => {
    if (!answersResponse || !onboardingStatusResponse) {
      return null;
    }

    return onboardingAPIAdapter.generateDataForApplicationDocumentationFromOnboardingAnswers(
      answersResponse,
      onboardingStatusResponse,
    );
  }, [answersResponse, onboardingStatusResponse]);

  const onSubmit = async (values: FormValuesModel) => {
    await verificationContext?.handleVerificationTabSubmitWithLoader(
      async () => {
        switch (values.submitActionType) {
          case 'save-back':
            {
              await submitApplicationDocumentationStep(values);
              Message.success(t('success_save'));
              onAction('save-back');
            }
            break;

          case 'save':
            {
              await submitApplicationDocumentationStep(values);
              Message.success(t('success_save'));
              updateAnswers({});
            }
            break;

          case 'submit': {
            await submitApplicationDocumentationStep(values).then(async () => {
              await onboardingAPI.submitApplicationForReview();
              await dispatch(updateActiveApplication());
            });

            onAction('submit');
            break;
          }

          default: {
            await submitApplicationDocumentationStep(values);
            Message.success(t('success_save'));
            updateAnswers({});
          }
        }
      },
    );
  };

  async function submitApplicationDocumentationStep(values: FormValuesModel) {
    const formattedBody =
      onboardingAPIAdapter.submitApplicationDocumentationStep(
        values,
        initialFormValues as FormValuesModel,
        onboardingData.clientGroupId as string,
        onboardingData.contactId,
        OnboardingEntryTypes.Contact,
      );

    await onboardingAPI.submitApplicationDocumentationStep(formattedBody);
  }

  return (
    <LoadingWrapper loading={answersLoader || onboardingStatusLoading}>
      {initialFormValues && (
        <ApplicationDocumentationForm
          canSendMessage
          showBackButton
          canSubmitForReview={!!isSubmittedForReview}
          disabled={isViewOnly}
          onSubmit={onSubmit}
          initialFormValues={initialFormValues}
          hideSubmitForReviewButton
        />
      )}
    </LoadingWrapper>
  );
};

export default ApplicationDocumentation;
